/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
'use client'

import Link from 'next/link'
import { MouseEvent, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react'

import Icon from '../../components/Icon'
import modalService from '../../service/modalService'

export type AccountNavItem = {
  id?: string | null
  title: string
  icon: string
  url?: string
  onClick?: () => void
}

type AccountNavProps = {
  isOpen: boolean
  title: string
  items: AccountNavItem[]
  onClose: () => void
  closeButtonLabel?: string
}

export default function AccountNav({
  isOpen,
  title,
  items,
  onClose,
  closeButtonLabel = 'Close',
}: AccountNavProps) {
  const [isCloseAnimationStarted, setIsCloseAnimationStarted] = useState(false)
  const modalRef = useRef<HTMLDialogElement>(null)

  const onAccountNavClose = useCallback(() => {
    if (!modalRef.current) return

    modalRef.current.classList.add('closeLeftToRight')
    setIsCloseAnimationStarted(true)
  }, [])

  const onCloseAnimationEnd = useCallback(() => {
    if (!modalRef.current || !isCloseAnimationStarted) return

    modalRef.current.close()
    setIsCloseAnimationStarted(false)

    onClose()
    modalService.changeModalState('accountNav', false)
  }, [isCloseAnimationStarted, onClose])

  const onModalClick = useCallback(
    (event: MouseEvent<HTMLDialogElement>) => {
      if (!modalRef.current) return

      const rect = modalRef.current.getBoundingClientRect()

      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        onAccountNavClose()
      }
    },
    [onAccountNavClose],
  )

  const onEscapeClose = useCallback(
    (e: SyntheticEvent<HTMLDialogElement>) => {
      e.preventDefault()

      onAccountNavClose()
    },
    [onAccountNavClose],
  )

  const onItemClick = useCallback(
    (item: AccountNavItem) => {
      item.onClick?.()
      onAccountNavClose()
    },
    [onAccountNavClose],
  )

  useEffect(() => {
    if (isOpen && modalRef.current != null) {
      modalRef.current.showModal()
      modalService.changeModalState('accountNav', true)
      modalRef.current.classList.add('openLeftToRight')
    }
  }, [isOpen])

  return (
    <dialog
      ref={modalRef}
      onAnimationEnd={onCloseAnimationEnd}
      onClick={onModalClick}
      onCancel={onEscapeClose}
      className='fixed bottom-0 top-0 max-h-none max-w-none backdrop:cursor-pointer md:ml-auto md:w-[536px]'
    >
      <div className='flex h-screen w-screen min-w-full flex-col py-5 pb-10 pt-14 md:w-[536px]'>
        <header className='flex justify-between px-12 pb-6'>
          <h4 className='h4'>{title}</h4>

          <button
            onClick={onAccountNavClose}
            className='static xl:absolute xl:right-6 xl:top-6'
            aria-label={closeButtonLabel}
          >
            <Icon name='MdClose' size={32} />
          </button>
        </header>

        <div className='flex grow flex-col overflow-y-auto'>
          <ul className='flex flex-col px-12'>
            {items.map(item => (
              <li
                key={item.id ?? item.url ?? item.title}
                className='border-b border-lightgray last:border-b-0'
              >
                {item.url ? (
                  <Link
                    href={item.url}
                    onClick={() => onItemClick(item)}
                    className='flex w-full items-center gap-4 py-6 text-left no-underline transition-opacity hover:opacity-80'
                  >
                    <Icon name={item.icon} size={26} />
                    <p>{item.title}</p>
                  </Link>
                ) : (
                  <button
                    type='button'
                    onClick={() => onItemClick(item)}
                    className='flex w-full items-center gap-4 py-6 text-left transition-opacity hover:opacity-80'
                  >
                    <Icon name={item.icon} size={26} />
                    <p>{item.title}</p>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </dialog>
  )
}
